import type { FinanceEntry } from "../api/types";
import { daysInMonth, diffDays, makeDate, type DateStr } from "./date";

export type DueStatus = "paid" | "overdue" | "today" | "soon" | "open";

/** Due date of an entry with a day ("dia 31" in a 30-day month = the 30th). */
export function dueDate(e: FinanceEntry): DateStr | null {
  if (!e.dueDay) return null;
  const [y, m] = e.month.split("-").map(Number);
  return makeDate(y, m, Math.min(e.dueDay, daysInMonth(y, m)));
}

/**
 * Paid / overdue / due today / due within 7 days / open. An unpaid entry with no day is
 * overdue once its month is over.
 */
export function dueStatus(e: FinanceEntry, today: DateStr): { status: DueStatus; days: number | null } {
  if (e.paid) return { status: "paid", days: null };
  const due = dueDate(e);
  if (!due) return { status: e.month < today.slice(0, 7) ? "overdue" : "open", days: null };
  const days = diffDays(today, due);
  if (days < 0) return { status: "overdue", days };
  if (days === 0) return { status: "today", days };
  if (days <= 7) return { status: "soon", days };
  return { status: "open", days };
}

/** Unpaid expenses already past due (the Finanças tab count). */
export function overdueExpenses(entries: FinanceEntry[], today: DateStr): FinanceEntry[] {
  return entries.filter((e) => e.kind === "expense" && dueStatus(e, today).status === "overdue");
}

export function sumCents(entries: FinanceEntry[]): number {
  return entries.reduce((total, e) => total + e.amountCents, 0);
}
